import { Dialog } from '@base-ui/react/dialog';
import { useRef } from 'react';

import { Button } from '@/shared/ui/button';

import { PlacementForm } from './placement-form';

import type { PlacementModuleView } from './placement-result';
import type { SubmitPlacement } from '@mio/shared';

/**
 * The sign-up questionnaire. Closing the dialog by any means is "decide later": the learner
 * keeps the whole curriculum and can choose a path from Studio at any time.
 */
export function PlacementDialog({
  open,
  modules,
  saving,
  skipping,
  error,
  onSave,
  onSkip,
}: {
  open: boolean;
  modules: PlacementModuleView[];
  saving: boolean;
  skipping: boolean;
  error: string | null;
  onSave: (input: SubmitPlacement) => void;
  onSkip: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const busy = saving || skipping;
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onSkip();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-[color-mix(in_oklab,var(--fg)_28%,transparent)] backdrop-blur-[2px]" />
        <Dialog.Popup
          initialFocus={heading}
          className="border-line bg-canvas text-fg fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[min(92vw,40rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border p-6 shadow-xl outline-none"
        >
          <Dialog.Title
            ref={heading}
            tabIndex={-1}
            className="text-lg font-semibold tracking-tight outline-none"
          >
            Where should you start?
          </Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-2 text-sm leading-6">
            Four modules build one application. Each stands on its own, so a few questions about
            what you can already do point you to the right one. You can change this later from
            Studio.
          </Dialog.Description>
          <div className="mt-5">
            <PlacementForm
              modules={modules}
              saving={saving}
              error={error}
              onSave={onSave}
              compact
              secondaryAction={
                <Button
                  type="button"
                  variant="secondary"
                  loading={skipping}
                  disabled={saving}
                  onClick={onSkip}
                >
                  Decide later
                </Button>
              }
            />
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
